import { CheckCircle, Target } from 'lucide-react';
import { useState } from 'react';

interface ActionItem {
  id: string;
  text: string;
  status: 'pending';
}

interface ActionPlanOfferProps {
  goal: string;
  actionItems: ActionItem[];
  timeline?: string;
  onAccept: () => void;
  onDismiss: () => void;
}

export function ActionPlanOffer({
  goal,
  actionItems,
  timeline,
  onAccept,
  onDismiss
}: ActionPlanOfferProps) {
  const [isAccepting, setIsAccepting] = useState(false);

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      await onAccept();
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gold-50 to-electric-50 rounded-xl border-2 border-gold-300 p-5 shadow-lg">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-gold-500 flex items-center justify-center flex-shrink-0">
          <Target className="h-5 w-5 text-ink-950" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-ink-950 text-lg mb-1">
            Action Plan Created
          </h3>
          <p className="text-gray-700 font-medium">{goal}</p>
          {timeline && (
            <p className="text-sm text-gray-600 mt-1">Timeline: {timeline}</p>
          )}
        </div>
      </div>

      <div className="space-y-2 mb-4">
        {actionItems.map((item, index) => (
          <div
            key={item.id}
            className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200"
          >
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-600">
              {index + 1}
            </div>
            <span className="text-gray-900 flex-1">{item.text}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleAccept}
          disabled={isAccepting}
          className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-gold-500 text-ink-950 rounded-lg hover:bg-gold-600 disabled:opacity-50 font-semibold transition-colors"
        >
          <CheckCircle className="h-5 w-5 mr-2" />
          {isAccepting ? 'Saving Plan...' : 'Accept & Track This Plan'}
        </button>
        <button
          onClick={onDismiss}
          disabled={isAccepting}
          className="px-4 py-3 text-gray-600 hover:text-gray-900 font-medium transition-colors"
        >
          Dismiss
        </button>
      </div>

      <p className="text-xs text-gray-600 mt-3 text-center">
        Click "Plans" above to track your progress anytime
      </p>
    </div>
  );
}
